import { Component, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableFunctionsService } from '@services/table/table-functions.service';
import '@angular/localize/init';
import { ComponentBase } from '@abstracts/component-base';
import { Injector } from '@angular/core';
import { StripeInfosService } from '@services/entity/stripe-infos.service';
import { PricingsService } from '@services/entity/pricings.service';
import { InvoiceDto } from '@dto/invoiceDto';
import { plainToClass } from 'class-transformer';
import { UsersService } from '@services/entity/users.service';
import { TransactionsService } from '@services/entity/transactions.service';
import { UserDto } from '@dto/userDto';
import { TransactionDto } from '@dto/transactionDto';
import { InvoicesService } from '@services/entity/invoices.service';
import { InvoiceSettingsDto } from '@dto/invoiceSettingsDto';
import { CustomersService } from '@services/entity/customers.service';
import { User } from 'oidc-client';
import { InvoiceStatusEnum } from '@enums/invoiceStatusEnum.enum';

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss']
})
export class InvoicesComponent extends ComponentBase implements OnInit {

  public className = 'InvoicesComponent';
  public activeTab = 'invoices';
  public activeComponentClassName: string;

  public isUser: boolean = false;
  public isCustomer: boolean = false;
  public isOperator: boolean = false;
  public isSupporter: boolean = false;

  @ViewChild('operatorView') operatorView: TemplateRef<any>;
  @ViewChild('customerView') customerView: TemplateRef<any>;
  @ViewChild('userView') userView: TemplateRef<any>;

  private ownerUserId: string
  private ownerCustomerId: number

  private invoiceSettings: InvoiceSettingsDto
  private user: UserDto;
  private users: UserDto[] = []
  private usersWithPricing: UserDto[] = []
  private transactions: TransactionDto[] = []
  private transactionsToBeBilled: TransactionDto[] = []

  public payoutAmountDue: number = 0

  constructor(
    public tableFunctions: TableFunctionsService,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public transactionsService: TransactionsService,
    public usersService: UsersService,
    public customersService: CustomersService,
    public pricingsService: PricingsService,
    public invoicesService: InvoicesService,
    public stripeInfosService: StripeInfosService,
    injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.determineTemplate();
    super.ngOnInit();
  }

  determineTemplate() {
    if (this.authenticationService.userHasRole('Operator')) this.isOperator = true;
    if (this.authenticationService.userHasRole('Customer')) this.isCustomer = true;
    if (this.authenticationService.userHasRole('User')) this.isUser = true;
    this.isSupporter = this.authenticationService.userHasRole('Supporter')

    if (this.isOperator) {
      this.ownerCustomerId = this.activatedRoute.parent.snapshot.params.key;
      this.customersService.getMasterUserOfCustomer(this.ownerCustomerId).subscribe(masterUser => {
        this.ownerUserId = masterUser.id;
        this.usersService.getUserDtoById(this.ownerUserId).subscribe(user => {
          this.user = plainToClass(UserDto, user);
          this.getInvoiceSettings();
          return this.customerView;
        })
      })
    }

    if (this.isCustomer) {
      this.ownerUserId = localStorage.getItem('userId')
      this.ownerCustomerId = localStorage.getItem('customerId') ? parseInt(localStorage.getItem('customerId')) : undefined
      this.getUser()
      return this.customerView;
    }
    
    if (this.isUser) {
      return this.userView;
    }
  }

  getUser() {
    this.usersService.getLoggedInUser().subscribe(user => {
      this.user = plainToClass(UserDto, user)
      this.getInvoiceSettings()
    })
  }

  async getInvoiceSettings() {
    if (!this.ownerUserId) return false
    this.invoicesService.getInvoiceSettingsOfUser(this.ownerUserId).subscribe(invoiceSettings => {
      this.invoiceSettings = plainToClass(InvoiceSettingsDto, invoiceSettings)
      this.getTransactions();
      this.getUsers();
      if (!(this.invoiceSettings && this.isInvoiceSettingsValid(this.invoiceSettings)) && !this.isSupporter) {
        this.router.navigate(['settings'], { relativeTo: this.activatedRoute })
      }

    }, error => console.error(error))
  }

  private isInvoiceSettingsValid(settings) {
    if (settings.vatId) {
      return true
    }
    return false
  }

  async getTransactions() {
    if (this.ownerCustomerId == undefined) return false
    this.transactionsService.getTransactionsOfCustomer(this.ownerCustomerId).subscribe(transactions => {
      this.transactions = plainToClass(TransactionDto, transactions)

      for (const transaction of this.transactions) {
        if (transaction.invoiceId == null && transaction.pricingId != null && transaction.price != null) {
          this.transactionsToBeBilled.push(transaction)
        }
      }
    })
  }

  getTransactionsToBeBilledOfUser(userId) {
    return this.transactionsToBeBilled.filter(t => t.userId == userId)
  }

  getMeterAmountSumForUser(userId) {
    let transactionsOfUser = this.getTransactionsToBeBilledOfUser(userId)
    let meterAmounts = transactionsOfUser.map(t => t.meterAmount)
    return meterAmounts.reduce((previous, current) => previous + current)
  }

  getPriceSumForUser(user) {
    let transactionsOfUser = this.getTransactionsToBeBilledOfUser(user.id)
    let meterAmounts = transactionsOfUser.map(t => t.meterAmount)
    let sum: number = 0
    for (const meterAmount of meterAmounts) {
      sum += parseFloat((meterAmount * user.pricing.price / 1000).toFixed(2))
    }
    return sum
  }

  async getUsers() {
    if(this.isOperator) {
      this.usersService.getUsersOfCustomerAsync(this.ownerCustomerId).then(users => {
        this.users = plainToClass(UserDto, users)
        
        for (const user of this.users) {
          if (user.pricingId != null) {
            this.usersWithPricing.push(user)
          }
        }
      });
    } else {
      this.usersService.getUsersOfCurrentCustomerAsync().then(users => {
        this.users = plainToClass(UserDto, users)
        
        for (const user of this.users) {
          if (user.pricingId != null) {
            this.usersWithPricing.push(user)
          }
        }
      });
    }
  }

  public onRouterOutletActivate(event: any) {
    this.activeComponentClassName = event.className;
  }
}
